/* cb-variant-sync-expanse-8.js
 * Expanse 8.0:
 * - Keeps [data-variant-color-label] synced to the selected color option (option1)
 * - Allows "preview selecting" sold-out swatches by forcing variant id + triggering theme updates
 * - Disables ATC when selected variant is unavailable
 */

(function () {
  let productCache = null;
  let fetching = false;

  function $(sel, root) { return (root || document).querySelector(sel); }
  function $all(sel, root) { return Array.from((root || document).querySelectorAll(sel)); }

  function setLabel(text) {
    const next = (text || "").trim();
    if (!next) return;
    $all("[data-variant-color-label]").forEach(el => {
      if (el.textContent.trim() !== next) el.textContent = next;
    });
  }

  function getProductJsonUrl() {
    const path = window.location.pathname.replace(/\/$/, "");
    return path + ".js";
  }

  async function fetchProduct() {
    if (productCache) return productCache;
    if (fetching) return null;

    fetching = true;
    try {
      const res = await fetch(getProductJsonUrl(), { credentials: "same-origin" });
      if (!res.ok) throw new Error("Product JSON fetch failed");
      productCache = await res.json();
      return productCache;
    } catch (e) {
      return null;
    } finally {
      fetching = false;
    }
  }

  function getCurrentVariantId() {
    // Expanse typically uses either URL param or the [name="id"] in the add-to-cart form
    try {
      const u = new URL(window.location.href);
      const v = u.searchParams.get("variant");
      if (v) return String(v).trim();
    } catch (e) {}

    const idEl = $('form[action^="/cart/add"] [name="id"]') || $('[name="id"]');
    if (!idEl) return null;
    return (idEl.value || idEl.getAttribute("value") || "").trim() || null;
  }

  function findVariantById(product, id) {
    if (!product || !product.variants || !id) return null;
    return product.variants.find(v => String(v.id) === String(id)) || null;
  }

  function findVariantByOption1(product, option1Text) {
    if (!product || !product.variants || !option1Text) return null;
    const key = String(option1Text).trim();
    return product.variants.find(v => String(v.option1 || "").trim() === key) || null;
  }

  function isVariantAvailable(variant) {
    // /products/<handle>.js returns variants with .available
    return !!(variant && variant.available);
  }

  function updateUrlVariantParam(variantId) {
    try {
      const url = new URL(window.location.href);
      url.searchParams.set("variant", String(variantId));
      window.history.replaceState({}, "", url.toString());
    } catch (e) {}
  }

  function setVariantIdInForm(variantId) {
    const idEl = $('form[action^="/cart/add"] [name="id"]') || $('[name="id"]');
    if (!idEl) return false;

    // select or input
    if (idEl.tagName === "SELECT") {
      idEl.value = String(variantId);
    } else {
      idEl.value = String(variantId);
      idEl.setAttribute("value", String(variantId));
    }

    // trigger theme listeners
    idEl.dispatchEvent(new Event("change", { bubbles: true }));
    idEl.dispatchEvent(new Event("input", { bubbles: true }));
    return true;
  }

  function setAddToCartDisabled(disabled) {
    const form = $('form[action^="/cart/add"]');
    if (!form) return;

    const btn =
      form.querySelector('button[name="add"]') ||
      form.querySelector('button[type="submit"]') ||
      form.querySelector('[data-add-to-cart]');

    if (!btn) return;

    btn.disabled = !!disabled;
    btn.setAttribute("aria-disabled", disabled ? "true" : "false");

    // Optional UX cue (safe): add a class for styling if you want later
    btn.classList.toggle("is-disabled-by-variant", !!disabled);

    // Sync button text natively since we bypass the theme's event listener for sold out variants
    const span = btn.querySelector('span') || btn;
    if (!btn.dataset.originalText && span.innerText.trim() !== "Sold Out") {
      btn.dataset.originalText = span.innerText.trim();
    }
    
    if (disabled) {
      if (span.innerText.trim() !== "Sold Out") {
        span.innerText = "Sold Out";
      }
    } else {
      const targetText = btn.dataset.originalText || "Add to cart";
      if (span.innerText.trim() !== targetText.trim()) {
        span.innerText = targetText;
      }
    }
  }

  async function syncLabelFromSelectedVariant() {
    const variantId = getCurrentVariantId();
    if (!variantId) return;

    const product = await fetchProduct();
    const v = findVariantById(product, variantId);
    if (!v) return;

    // Update label to option1 (color)
    if (v.option1) setLabel(v.option1);

    // Ensure ATC matches availability
    setAddToCartDisabled(!isVariantAvailable(v));
  }

  function getSwatchValueFromClickTarget(target) {
    // Expanse 8 swatches are usually labels with for="" pointing to an input
    const label = target.closest("label");
    if (!label) return null;

    const forId = label.getAttribute("for");
    let input = null;

    if (forId) input = document.getElementById(forId);
    if (!input) input = label.querySelector('input[type="radio"]');
    if (!input) return null;

    // We only care about option1/color group if it exists
    // Expanse 8 often uses data-index="option1"
    const isColor =
      input.getAttribute("data-index") === "option1" ||
      input.name === "options[Option]" ||               // your case in screenshots
      (input.name || "").includes("options[");

    if (!isColor) return null;

    // Sold-out swatches in Expanse often store value in data-disabled-value
    const val =
      (input.getAttribute("data-disabled-value") || "").trim() ||
      (input.getAttribute("data-value") || "").trim() ||
      (input.value || "").trim();

    if (!val) return null;

    // Determine sold-out state from label/input
    const soldOut =
      input.disabled ||
      label.classList.contains("disabled") ||
      label.classList.contains("is-disabled") ||
      label.getAttribute("aria-disabled") === "true" ||
      !!label.querySelector(".element-swatch--disabled") ||
      !!label.querySelector(".element-swatch__item--disabled");

    return { value: val, soldOut, input, label };
  }

  async function forceSelectByOption1(option1Value) {
    const product = await fetchProduct();
    const variant = findVariantByOption1(product, option1Value);
    if (!variant) return;

    // Force variant selection so Expanse updates media/price/etc
    updateUrlVariantParam(variant.id);
    setVariantIdInForm(variant.id);

    // Update label immediately
    setLabel(variant.option1 || option1Value);

    // Disable ATC if sold out
    setAddToCartDisabled(!isVariantAvailable(variant));
  }

  // ---- Events ----

  document.addEventListener("DOMContentLoaded", function () {
    syncLabelFromSelectedVariant();
  });

  // When options change normally (in-stock), keep label synced
  document.addEventListener("change", function (e) {
    const t = e.target;
    if (!t) return;

    if (
      t.matches('input[type="radio"][name^="options["]') ||
      t.matches('select[name^="options["]') ||
      t.matches('form[action^="/cart/add"] [name="id"]') ||
      t.matches('[name="id"]')
    ) {
      requestAnimationFrame(syncLabelFromSelectedVariant);
      setTimeout(syncLabelFromSelectedVariant, 60);
    }
  });

  // Handle clicks on swatches (including sold-out)
  document.addEventListener("click", function (e) {
    const info = getSwatchValueFromClickTarget(e.target);
    if (!info) return;

    // Always update label based on what was clicked
    setLabel(info.value);

    // If sold out, Expanse may block selection -> force select variant so media updates
    if (info.soldOut) {
      e.preventDefault(); // prevent theme trying and failing in weird ways
      e.stopPropagation();

      // Ensure the underlying radio button is checked so the UI remains in sync.
      // This is necessary so that later clicking an in-stock variant triggers a native 'change' event.
      info.input.checked = true;

      forceSelectByOption1(info.value);
      return;
    }

    // In-stock: let theme do its thing, then sync from selected variant
    requestAnimationFrame(syncLabelFromSelectedVariant);
    setTimeout(syncLabelFromSelectedVariant, 60);
  }, true);

  // If Expanse re-renders variant picker, keep things synced
  const obs = new MutationObserver(function () {
    syncLabelFromSelectedVariant();
  });

  document.addEventListener("DOMContentLoaded", function () {
    obs.observe(document.documentElement, { childList: true, subtree: true });
  });
})();
